import type { ForqSupabaseClient } from './client';
import type { ClosePeriodInput, PeriodClosingGateway, PeriodClosure } from '@forq/finance-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

/**
 * Periodenabschluss gegen Supabase.
 *
 * closePeriod schickt den in TS gerechneten Snapshot mit; die DB friert ihn ein und gibt den Abschluss
 * als jsonb GENAU in der Form von PeriodClosure zurueck (camelCase, snapshot verschachtelt) -- ein Test
 * in periodClosing.test.ts sichert die Gestalt. reopenPeriod entfernt die Sperre wieder.
 */
export class SupabasePeriodClosingGateway implements PeriodClosingGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  private async rpc(functionName: string, args: Record<string, unknown>): Promise<unknown> {
    const { data, error } = await this.client.rpc(functionName, args);
    if (error) throw new SupabaseOperationError(`finance.${functionName}`, messageOf(error));
    return data;
  }

  async listClosures(tenantId: string): Promise<readonly PeriodClosure[]> {
    const data = await this.rpc('finance_period_closure_list', { p_tenant_id: tenantId });
    return (data ?? []) as readonly PeriodClosure[];
  }

  async closePeriod(input: ClosePeriodInput): Promise<PeriodClosure> {
    const { tenantId, period, ...rest } = input;
    // Der Zeitraum wird flach in from/to gelegt; der Snapshot geht als verschachteltes Objekt mit.
    return this.rpc('finance_period_closure_close', {
      p_tenant_id: tenantId,
      p_payload: { ...rest, from: period.from, to: period.to },
    }) as Promise<PeriodClosure>;
  }

  async reopenPeriod(tenantId: string, closureId: string): Promise<void> {
    await this.rpc('finance_period_closure_reopen', { p_tenant_id: tenantId, p_id: closureId });
  }
}
